import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { ordinalAlphabeticBy } from '@app/utils/ordinal'
import { useHeightStore } from '@app/shared/vue/store/height-store'

export const useBubbleStore = defineStore('bubble', () => {
  const highlightedPath = ref('')
  // By keeping track of the bubbles per section, we can link them to one another by path
  // and derive useful reactive properties out of this linking relationship
  const inputBubblesPerPath = ref<Record<string, boolean>>({})
  const inputBubblesOrder = ref<string[]>([])
  const remarkBubblesPerPath = ref<Record<string, { id: string }>>({})

  const heightStore = useHeightStore()
  const scrollOffset = computed(() =>
    [
      heightStore.stickyHeaderHeight,
      heightStore.mainSubheaderHeight,
      heightStore.secondarySubheaderHeight,
    ].reduce((acc, height) => acc + parseInt(height.replace('px', '')), 24)
  )

  function registerBubble(type: 'input', params: { linkingPath: string }): void
  function registerBubble(
    type: 'remark',
    params: { id: string; linkingPath: string }
  ): void

  function registerBubble(
    type: 'input' | 'remark',
    {
      id,
      linkingPath,
    }: {
      id?: string
      linkingPath: string
    }
  ): void {
    if (type === 'input') {
      inputBubblesPerPath.value[linkingPath] = true
    } else {
      remarkBubblesPerPath.value[linkingPath] = { id: id ?? '' }
    }
  }

  function unregisterBubble(
    type: 'input' | 'remark',
    linkingPath: string
  ): void {
    if (type === 'input') {
      delete inputBubblesPerPath.value[linkingPath]
    } else {
      delete remarkBubblesPerPath.value[linkingPath]
    }
  }

  function registerBubbleOrder(path: string): void {
    if (!inputBubblesOrder.value.includes(path)) {
      inputBubblesOrder.value.push(path)
    }
  }

  const ordinal = computed(() => {
    return inputBubblesOrder.value.reduce((acc, path, index) => {
      acc[path] = ordinalAlphabeticBy(index + 1)
      return acc
    }, {} as Record<string, string>)
  })

  function setHighlightedBubble(path: string): void {
    highlightedPath.value = path
  }

  return {
    inputBubblesPerPath,
    remarkBubblesPerPath,
    highlightedPath,
    registerBubble,
    unregisterBubble,
    registerBubbleOrder,
    ordinal,
    setHighlightedBubble,
    scrollOffset,
  }
})
